import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow as UITableRow,
} from "@/components/ui/table";
import { DocumentInfo } from "@/types/document";
import { TableComponent } from "@/types/insights";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { useMemo, useState } from "react";
import { ComponentCard } from "./InsightsComponentCard";

const toSortableNumber = (value: string) => {
  const normalized = value.replace(/,/g, "").trim();
  if (!normalized || normalized === "-" || normalized === "—") return null;

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const compareTableCellValues = (left: string, right: string) => {
  const leftNumber = toSortableNumber(left);
  const rightNumber = toSortableNumber(right);

  if (leftNumber !== null && rightNumber !== null) {
    return leftNumber - rightNumber;
  }

  if (leftNumber !== null) return -1;
  if (rightNumber !== null) return 1;

  return left.localeCompare(right, undefined, { numeric: true, sensitivity: "base" });
};

export const InsightsTable = ({
  component,
  documentsById,
  flaggingByDocumentId,
  onFlagDocument,
}: {
  component: TableComponent;
  documentsById: Map<string, DocumentInfo>;
  flaggingByDocumentId: Record<string, boolean>;
  onFlagDocument: (documentId: string) => Promise<void>;
}) => {
  const columns = component.columns;
  const rows = component.rows;
  const [sortColumnIndex, setSortColumnIndex] = useState<number | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const hasDocumentRows = rows.some((row) => row.itemType === "document" && row.itemId);

  const sortedRows = useMemo(() => {
    if (sortColumnIndex === null) return rows;

    return [...rows].sort((left, right) => {
      const comparison = compareTableCellValues(
        left.cells[sortColumnIndex] || "",
        right.cells[sortColumnIndex] || ""
      );

      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [rows, sortColumnIndex, sortDirection]);

  if (columns.length === 0) return null;

  return (
    <ComponentCard title={component.title} description={component.description}>
      <Table>
        <TableHeader>
          <UITableRow>
            {columns.map((column, index) => (
              <TableHead key={`${column}-${index}`}>
                <Button
                  type="button"
                  variant="ghost"
                  className="-ml-3 h-auto px-3 py-1 font-medium text-muted-foreground"
                  onClick={() => {
                    if (sortColumnIndex === index) {
                      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
                      return;
                    }

                    setSortColumnIndex(index);
                    setSortDirection("asc");
                  }}
                  aria-sort={
                    sortColumnIndex === index
                      ? sortDirection === "asc"
                        ? "ascending"
                        : "descending"
                      : "none"
                  }
                >
                  {column}
                  {sortColumnIndex !== index ? (
                    <ArrowUpDown className="size-4" />
                  ) : sortDirection === "asc" ? (
                    <ArrowUp className="size-4" />
                  ) : (
                    <ArrowDown className="size-4" />
                  )}
                </Button>
              </TableHead>
            ))}
            {hasDocumentRows ? <TableHead className="w-28 text-right">Actions</TableHead> : null}
          </UITableRow>
        </TableHeader>
        <TableBody>
          {sortedRows.map((row, rowIndex) => (
            <UITableRow key={rowIndex}>
              {columns.map((_, columnIndex) => (
                <TableCell key={`${rowIndex}-${columnIndex}`} className="align-top">
                  <span className="break-words">{row.cells[columnIndex] || "—"}</span>
                </TableCell>
              ))}
              {hasDocumentRows ? (
                <TableCell className="text-right">
                  {row.itemType === "document" && row.itemId ? (
                    <Button
                      size="sm"
                      variant={
                        documentsById.get(row.itemId)?.status === "flagged"
                          ? "secondary"
                          : "outline"
                      }
                      disabled={Boolean(flaggingByDocumentId[row.itemId])}
                      onClick={() => void onFlagDocument(row.itemId!)}
                    >
                      {flaggingByDocumentId[row.itemId] ? <Spinner className="size-3" /> : null}
                      {documentsById.get(row.itemId)?.status === "flagged" ? "Remove flag" : "Flag"}
                    </Button>
                  ) : null}
                </TableCell>
              ) : null}
            </UITableRow>
          ))}
        </TableBody>
      </Table>
    </ComponentCard>
  );
};
